/*
 * "ALONG THE BOTTOM BAR" > FISHERMAN: the silhouette of a man in a small rowboat, with the bottom metrics bar as his sea.
 *
 * A port of design-prototypes/empty-state-motion/src/fisherman/fisherman.js (the mock Chris approved to build, and asked to
 * be "a little smaller"), with two differences that the real page needs:
 *   1. The mock lives in a fixed 1000-unit stage. Here the stage is whatever width the real footer is, and how big he is and
 *      where he stops come from fishermanPlan(). The boat FLOATS ABOVE the footer's numbers, so it may sit over the metrics
 *      horizontally; what must never reach a number is anything BELOW the surface, and that is enforced by construction:
 *      everything he draws is clipped to `maxBelow` units under the footer's top edge, which the caller sets from the real
 *      gap between that edge and the first line of text (see drawFisherman).
 *   2. He is drawn into a canvas that is laid over the top edge of the footer (ambientLife.ts owns that canvas, its fade
 *      and its schedule), not into a replica.
 * If you change how he LOOKS, change the mock's fisherman.js too (and republish its sheet): they are two copies of one drawing.
 *
 * WHAT HE DOES. He rows in along the top edge of the bar from the left or the right edge, as far as the middle of the page
 * or a little past it, ships his oars, takes up a rod, and then FISHES FOR AS LONG AS THE SITE STAYS QUIET: a pattern of
 * casts, some of which land a small fish that he lets go and some of which nothing bites, repeating for ever (he "enjoys
 * fishing and can do it for a long time"). He casts toward the edge he came from (a rower faces the stern). Everything is an
 * ANALYTIC function of time (no state), so any moment can be drawn without running a loop.
 *
 * COLOUR. One flat slate blue, the birds' slate (#566b80 in the ghost layer) made lighter at Chris's request, twice (#788999, then #93a1ad):
 * no outline, no second tone, no accent. (The float and the fish under the water are pale so that they show on the dark bar.)
 *
 * Stage units: the sea surface is y = SURF (the footer's top edge). Boat frame: origin = the middle of the boat at the
 * waterline, +x = toward the man's front (the stern), y up is negative.
 */

export const FISHERMAN_SC = 0.65;         // boat-frame units to stage units (1.0, then 0.8, then 0.65: Chris asked for him smaller twice)
export const FISHERMAN_SURF = 533;        // the sea surface, in stage units (the replica's bar top edge)
const BARB = 572;                         // the bottom of the bar in the replica; only the top few units are ever drawn on
/** how far above the surface the canvas must reach (the rod tip, the float's arc), and how far below (the reflection) */
export const FISHERMAN_ABOVE = 125;
export const FISHERMAN_BELOW = 10;

const INK = "#93a1ad", INK2 = INK, LINE = "rgba(147,161,173,0.85)", PALE = "rgba(232,240,246,0.85)";
const SC = FISHERMAN_SC, SURF = FISHERMAN_SURF;
const L1 = 20.5, L2 = 20.5, ANK = 3.2, SHO = [0.8, -24.6], UA = 15.6, FA = 14.6;
const HIP = [-2, -12.6], PIN = [6, -13.2], LOUT = 82, KIN = 0.42, REACH = 108;
const T_STROKE = 2.8, N_STROKES = 3, TAU = 2.6;   // calm: about 21 strokes a minute; the last drive, then a glide to a stop

export type Pt = [number, number];
export interface FishermanScript {
  dir: 1 | -1; fs: number; X_STOP: number; S0: number; pos: number[]; step: number; strokes: number;
  tDriveEnd: number; tStop: number; tPick: number;
  /** when the first cast begins; and, for the FIRST cycle only, the landing, the bite and when the next cast begins (for the mock's controls) */
  tc: number; tl: number; tb: number; tc2: number;
}

/* ───────── small helpers ───────── */
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (x: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, x));
const ease = (k: number) => { const c = clamp(k); return c * c * (3 - 2 * c); };
const smooth = ease;
function keys(t: number, list: number[][]): number {   // [[t0, v0], [t1, v1], ...] eased between
  if (t <= list[0][0]) return list[0][1];
  for (let i = 0; i < list.length - 1; i++) if (t <= list[i + 1][0]) return lerp(list[i][1], list[i + 1][1], smooth((t - list[i][0]) / (list[i + 1][0] - list[i][0])));
  return list[list.length - 1][1];
}
function ik(hx: number, hy: number, fx: number, fy: number, l1: number, l2: number, bend: number): Pt {   // two-bone solve; bend +1: forward/up (a knee), -1: backward/down (an elbow)
  const dx = fx - hx, dy = fy - hy;
  let d = Math.hypot(dx, dy);
  d = Math.min(Math.max(d, Math.abs(l1 - l2) + 0.01), l1 + l2 - 0.01);
  const a = Math.atan2(dy, dx), th = Math.acos(clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1)), ka = a - bend * th;
  return [hx + Math.cos(ka) * l1, hy + Math.sin(ka) * l1];
}
function limb(ctx: CanvasRenderingContext2D, x1: number, y1: number, r1: number, x2: number, y2: number, r2: number) {   // a tapered capsule
  const a = Math.atan2(y2 - y1, x2 - x1), c = Math.cos(a + Math.PI / 2), s = Math.sin(a + Math.PI / 2);
  ctx.beginPath(); ctx.moveTo(x1 + c * r1, y1 + s * r1); ctx.lineTo(x2 + c * r2, y2 + s * r2);
  ctx.arc(x2, y2, r2, a + Math.PI / 2, a - Math.PI / 2, true); ctx.lineTo(x1 - c * r1, y1 - s * r1);
  ctx.arc(x1, y1, r1, a - Math.PI / 2, a + Math.PI / 2, true); ctx.closePath(); ctx.fill();
}
const torsoToBoat = (lean: number, v: Pt): Pt => { const c = Math.cos(lean), s = Math.sin(lean); return [HIP[0] + v[0] * c - v[1] * s, HIP[1] + v[0] * s + v[1] * c]; };
const boatToTorso = (lean: number, p: Pt): Pt => { const c = Math.cos(-lean), s = Math.sin(-lean), x = p[0] - HIP[0], y = p[1] - HIP[1]; return [x * c - y * s, x * s + y * c]; };

/* ───────── where he stops ─────────
   Chris: "he needs to row out more, like to the middle of the page or more." The boat floats ABOVE the footer's numbers, so
   there is no reason to keep him at the edge: he rows in to MIDDLE..PAST_MIDDLE of the page's width, measured from the edge he
   came in by. His size follows the page (u0), never the free space beside the metrics. */
const MIN_WIDTH = 560;
export const FISHERMAN_STOP_MIN = 0.5, FISHERMAN_STOP_MAX = 0.65;
export interface FishermanSide { u: number; xStop: number; width: number }   // u: stage units to CSS pixels; xStop and width in stage units
export interface FishermanPlan { left: FishermanSide | null; right: FishermanSide | null; u0: number }
/**
 * @param width the footer's width, CSS px
 * @param stop  how far across the page he rows, as a fraction of its width, from the edge he comes in by (0.5 = the middle)
 * A footer under MIN_WIDTH has no room for a sea at all.
 */
export function fishermanPlan(width: number, stop: number): FishermanPlan {
  const u0 = clamp(width / 1000, 0.8, 1.6);
  if (!(width >= MIN_WIDTH)) return { left: null, right: null, u0 };
  const f = clamp(stop, FISHERMAN_STOP_MIN, FISHERMAN_STOP_MAX), W = width / u0;
  return { left: { u: u0, xStop: W * f, width: W }, right: { u: u0, xStop: W * (1 - f), width: W }, u0 };
}

/* ───────── what he does once he is out there: casts that repeat for ever ─────────
   A cycle starts with a cast. In a CATCH cycle a fish takes the float after `wait` seconds, he plays it, holds it up and lets it
   go; in an EMPTY cycle nothing bites and he reels in. Either way a cycle ENDS with the line hanging and the rod at its ready
   pose, which is where a cast begins, so the pattern joins to itself and can repeat without a seam. */
type CycleKind = "catch" | "empty";
const PATTERN: { kind: CycleKind; wait: number }[] = [
  { kind: "catch", wait: 8.6 }, { kind: "empty", wait: 11 }, { kind: "catch", wait: 13 }, { kind: "empty", wait: 9 }, { kind: "empty", wait: 15 },
];
const CYCLES = PATTERN.map((c) => ({ ...c, len: 1.7 + c.wait + (c.kind === "catch" ? 7.4 : 3.4) }));
const PATTERN_LEN = CYCLES.reduce((n, c) => n + c.len, 0);
interface CycleAt { kind: CycleKind; wait: number; len: number; r: number; start: number; index: number }
function cycleAt(S: FishermanScript, t: number): CycleAt | null {
  if (t < S.tc) return null;
  const rel = t - S.tc, pat = Math.floor(rel / PATTERN_LEN), u = rel - pat * PATTERN_LEN;
  let acc = 0, i = 0;
  for (; i < CYCLES.length; i++) { if (u < acc + CYCLES[i].len) break; acc += CYCLES[i].len; }
  if (i >= CYCLES.length) { i = CYCLES.length - 1; acc = PATTERN_LEN - CYCLES[i].len; }   // floating point, at the very end of a pattern
  const c = CYCLES[i];
  return { kind: c.kind, wait: c.wait, len: c.len, r: u - acc, start: S.tc + pat * PATTERN_LEN + acc, index: i };
}

/* ───────── the script ───────── */
/** dir +1: the boat comes in from the LEFT edge (so he faces left); -1: from the right */
export function fishermanScript(dir: 1 | -1, stage?: { width: number; xStop: number }): FishermanScript {
  const W = stage ? stage.width : 1000, fs = -dir, X_STOP = stage ? stage.xStop : dir > 0 ? 560 : 440, S0 = dir > 0 ? -105 : W + 105;   // far enough off the edge that the whole boat, oars out, starts out of sight
  // a calm stroke carries the boat about 100 units, so a longer row takes more strokes (never fewer than three)
  const strokes = clamp(Math.round(Math.abs(X_STOP - S0) / 100), 3, 12);
  const tDriveEnd = strokes * T_STROKE + 0.42 * T_STROKE, step = 0.02;
  // the boat's way, integrated from its speed: surges on each drive, slows on each recovery, then glides to a stop
  const pos = [0], vEnd = 1 + 0.6 * Math.cos(6.2832 * (0.42 - 0.21)), n = Math.ceil((tDriveEnd + TAU) / step);
  let total = 0;
  for (let i = 1; i <= n; i++) {
    const t = i * step;
    let v: number;
    if (t < tDriveEnd) { const u = (t / T_STROKE) % 1; v = 1 + 0.6 * Math.cos(6.2832 * (u - 0.21)); }
    else { const g = Math.max(0, 1 - (t - tDriveEnd) / TAU); v = vEnd * g * g; }
    total += v * step; pos.push(total);
  }
  const V0 = Math.abs(X_STOP - S0) / total;
  for (let i = 0; i < pos.length; i++) pos[i] *= V0;
  const tStop = tDriveEnd + TAU, tPick = tDriveEnd + 1.5, tc = tStop + 0.8;
  return { dir, fs, X_STOP, S0, pos, step, strokes, tDriveEnd, tStop, tPick, tc, tl: tc + 1.7, tb: tc + 1.7 + CYCLES[0].wait, tc2: tc + CYCLES[0].len };
}
function boatX(S: FishermanScript, t: number): number {
  if (t <= 0) return S.S0;
  const f = t / S.step, i = Math.floor(f);
  if (i >= S.pos.length - 1) return S.X_STOP;
  return S.S0 + S.dir * lerp(S.pos[i], S.pos[i + 1], f - i);
}
interface Frame { X: number; fs: number; y: number; pitch: number }
function frameAt(S: FishermanScript, t: number): Frame {   // where the boat is in the world, and how it sits on the water
  const u = (t / T_STROKE) % 1, rowing = t < S.tDriveEnd;
  return {
    X: boatX(S, t), fs: S.fs,
    y: SURF + 0.55 * Math.sin(t * 1.05) + 0.25 * Math.sin(t * 1.7 + 1.3) + (rowing ? 0.5 * Math.sin(6.2832 * u) : 0),
    pitch: 0.013 * Math.sin(t * 0.8 + 0.5) + (rowing ? 0.016 * Math.sin(6.2832 * (u + 0.1)) : 0),
  };
}
function toWorld(F: Frame, lx: number, ly: number): Pt { const c = Math.cos(F.pitch), s = Math.sin(F.pitch); return [F.X + F.fs * (lx * c - ly * s) * SC, F.y + (lx * s + ly * c) * SC]; }

/* ───────── the oars: a lever through the oarlock ───────── */
interface Oar { psi: number; blade: Pt; handle: Pt }
function oarAt(S: FishermanScript, t: number): Oar {   // psi: how far the blade is swept (negative: toward the bow at the catch; positive: toward the stern at the finish)
  const DRIVE = 0.42, deg = Math.PI / 180;
  const stroke = (tt: number) => {
    const u = (tt / T_STROKE) % 1;
    if (u < DRIVE) return { psi: lerp(-50 * deg, 50 * deg, u / DRIVE), by: 1.8 };
    const w = (u - DRIVE) / (1 - DRIVE);
    return { psi: lerp(50 * deg, -50 * deg, smooth(w)), by: 1.8 - 5.6 * Math.sin(Math.PI * w) };
  };
  let psi: number, by: number;
  if (t < S.tDriveEnd) { const s0 = stroke(t); psi = s0.psi; by = s0.by; }
  else {
    const end = stroke(S.tDriveEnd - 0.0001), r = smooth((t - S.tDriveEnd - 0.1) / 1.1);   // the oars come out of the water and lie along the side
    psi = lerp(end.psi, -88 * deg, r); by = lerp(Math.min(end.by, -1.5), -15.5, r);
  }
  return { psi, blade: [PIN[0] + LOUT * Math.sin(psi), by], handle: [PIN[0] - KIN * LOUT * Math.sin(psi), PIN[1] + KIN * (PIN[1] - by)] };
}

/* ───────── cast timing, relative to the start of a cast ───────── */
const castA = (r: number) => keys(r, [[0, 0.84], [0.55, 1.86], [0.68, 1.86], [0.98, 0.3], [1.7, 0.74]]);
const castGrip = (r: number): Pt => [keys(r, [[0, 0], [0.5, -2.8], [0.68, -2.8], [0.98, 2.0], [1.7, 0]]), keys(r, [[0, 0], [0.5, -11.5], [0.68, -11.5], [0.98, -1.5], [1.7, 0]])];
const castLean = (r: number) => keys(r, [[0, 0.1], [0.55, -0.06], [0.68, -0.06], [0.98, 0.2], [1.7, 0.1]]);

interface Rod { gx: number; gy: number; a: number; bend: number; back: number; front: number; reel: number; alpha: number; tip?: number[] }
interface Pose { breathe: number; oar: Oar; rodAlpha: number; stowAlpha: number; lean: number; headDown: number; rod: Rod; handNear: Pt; handFar: Pt }
function poseAt(S: FishermanScript, t: number): Pose {
  const o = oarAt(S, t), tDE = S.tDriveEnd, toRod = smooth((t - S.tPick) / 0.8);
  // the body: leans with the stroke while rowing, then settles forward over the rod
  const psiN = (o.psi + 0.873) / 1.745, rowLean = lerp(0.26, -0.34, clamp(psiN)), settle = smooth((t - (tDE + 0.5)) / 0.9);
  let lean = lerp(rowLean, 0.1, settle), headDown = lerp(-0.03, 0.14, settle);
  let a = 0.84, gx = 8.4, gy = -14.5, bend = 0.15, reel = 0;
  const c = cycleAt(S, t);
  if (c) {
    const r = c.r, rb = 1.7 + c.wait;
    if (r < 1.7) {   // the cast
      const g = castGrip(r);
      a = castA(r); lean = castLean(r); gx += g[0]; gy += g[1];
      headDown = lerp(0.14, -0.06, smooth(r / 0.5)) * (r < 1 ? 1 : lerp(1, 0.4, r - 1)); bend = r < 0.6 ? 0.15 : r < 1.0 ? 0.8 : 0.25;
    } else if (c.kind === "catch" && r >= rb + 0.25) {   // the fight and the catch
      const strike = smooth((r - (rb + 0.25)) / 0.3), settle2 = smooth((r - (rb + 3.4)) / 0.9), toss = smooth((r - (rb + 4.7)) / 0.5);
      a = lerp(lerp(0.74, 1.12, strike), 0.9, settle2); bend = lerp(1.0, 0.25, settle2); lean = lerp(0.1, -0.02, strike) + 0.12 * settle2;
      headDown = lerp(0.14, 0.32, settle2) * (1 - toss) + 0.1 * toss;
      reel = 9.5 * clamp(r - rb - 0.6, 0, 2.7);
    } else if (c.kind === "empty" && r >= rb && r < rb + 2.0) {   // nothing bit: he reels the float in
      const k = smooth((r - rb) / 0.5) * (1 - smooth((r - rb - 1.5) / 0.5));
      a = lerp(0.84, 0.7, k); bend = 0.15 + 0.2 * k; headDown = lerp(0.14, 0.2, k); reel = 9.5 * (r - rb);
    }
    // the last moments of a cycle ease back to the ready pose a cast begins from, so the pattern has no seam
    const prep = smooth((r - (c.len - 1.2)) / 1.2);
    if (prep > 0) { a = lerp(a, 0.84, prep); lean = lerp(lean, 0.1, prep); headDown = lerp(headDown, 0.14, prep); bend = lerp(bend, 0.15, prep); }
  }
  const rod: Rod = { gx, gy, a, bend, back: 10, front: 70, reel, alpha: toRod };
  // hands: on the oar handles, then resting on the knees, then on the rod
  const handleT = boatToTorso(lean, [o.handle[0] + 0.8, o.handle[1] + 0.2]), handleF = boatToTorso(lean, [o.handle[0] - 0.6, o.handle[1] + 1.4]);
  const lap: Pt = [10.5, -3.5], lapF: Pt = [8.0, -2.5];
  const u = [Math.cos(a), -Math.sin(a)], farGrip = [gx + u[0] * 13, gy + u[1] * 13];
  const off = smooth((t - (tDE + 1.0)) / 0.5);   // the hands let go of the oars
  return {
    breathe: Math.sin(t * 1.9), oar: o, rodAlpha: toRod, stowAlpha: 1 - toRod, lean, headDown, rod,
    handNear: [lerp(lerp(handleT[0], lap[0], off), gx, toRod), lerp(lerp(handleT[1], lap[1], off), gy, toRod)],
    handFar: [lerp(lerp(handleF[0], lapF[0], off), farGrip[0], toRod), lerp(lerp(handleF[1], lapF[1], off), farGrip[1], toRod)],
  };
}

/* ───────── drawing: the boat, then the man in it ───────── */
function hullPath(ctx: CanvasRenderingContext2D) {
  ctx.beginPath(); ctx.moveTo(-84, -16.5);                               // the bow's stem, raised
  ctx.bezierCurveTo(-70, -12.8, -40, -11.6, -4, -11.3);                  // the sheer line
  ctx.bezierCurveTo(30, -11.3, 58, -12.0, 79, -13.4);                    // ... to the stern
  ctx.lineTo(75.5, 1.8);                                                 // the transom
  ctx.bezierCurveTo(60, 3.6, 30, 4.3, -2, 4.5);                          // the bottom: a shallow skiff, so it never reaches the numbers in the bar
  ctx.bezierCurveTo(-36, 4.3, -66, 2.8, -80, -3.5);
  ctx.bezierCurveTo(-83.5, -8.5, -85, -13, -84, -16.5);                  // the stem
  ctx.closePath();
}
function drawBoat(ctx: CanvasRenderingContext2D, p: Pose) {
  ctx.fillStyle = INK; ctx.strokeStyle = INK;
  // above the waterline: solid; below it the hull shows through the water, paler
  ctx.save(); ctx.beginPath(); ctx.rect(-100, -60, 200, 60); ctx.clip(); hullPath(ctx); ctx.fill(); ctx.restore();
  ctx.save(); ctx.beginPath(); ctx.rect(-100, 0, 200, 20); ctx.clip(); ctx.globalAlpha = 0.5; hullPath(ctx); ctx.fill(); ctx.restore();
  // the seat and the oarlock
  ctx.fillRect(-13, -9.6, 26, 2.2);
  ctx.beginPath(); ctx.arc(PIN[0], PIN[1] + 0.2, 1.6, 0, 6.2832); ctx.fill(); ctx.fillRect(PIN[0] - 0.9, PIN[1], 1.8, 3);
  // the rod, lying in the bottom of the boat until he picks it up
  if (p.stowAlpha > 0.01) { ctx.save(); ctx.globalAlpha = p.stowAlpha; ctx.lineCap = "round"; ctx.lineWidth = 1.3; ctx.beginPath(); ctx.moveTo(30, -6.2); ctx.lineTo(-52, -6.9); ctx.lineWidth = 1.0; ctx.lineTo(-76, -10.5); ctx.stroke(); ctx.restore(); }
}
function drawOar(ctx: CanvasRenderingContext2D, o: Oar) {
  const bx = o.blade[0], by = o.blade[1], hx = o.handle[0], hy = o.handle[1], a = Math.atan2(by - hy, bx - hx);
  const blade = () => { ctx.beginPath(); ctx.moveTo(-14, -0.9); ctx.bezierCurveTo(-8, -3.2, 4, -3.4, 8.5, 0); ctx.bezierCurveTo(4, 3.4, -8, 3.2, -14, 0.9); ctx.closePath(); };
  ctx.save(); ctx.lineCap = "round"; ctx.lineJoin = "round";
  // a thin knock-out edge first: the oar lies across the hull, which is the same colour, so without it the oar would vanish into the boat.
  // (It is the page's white, so against the page it is invisible, and the figure stays one colour.)
  ctx.strokeStyle = "rgba(255,255,255,0.6)"; ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.lineWidth = 3.0;
  ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(bx, by); ctx.stroke();
  ctx.save(); ctx.translate(bx, by); ctx.rotate(a); blade(); ctx.lineWidth = 1.8; ctx.stroke(); ctx.restore();
  ctx.strokeStyle = INK; ctx.fillStyle = INK; ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(bx, by); ctx.stroke();
  ctx.save(); ctx.translate(bx, by); ctx.rotate(a); blade(); ctx.fill(); ctx.restore();   // the blade: a long leaf at the end
  ctx.restore();
}
function drawRod(ctx: CanvasRenderingContext2D, rod: Rod) {   // grip in the torso frame, a = radians above horizontal
  const u = [Math.cos(rod.a), -Math.sin(rod.a)], nrm = [-u[1], u[0]], N = 18, pts: number[][] = [];
  for (let i = 0; i <= N; i++) {
    const s = i / N, along = lerp(-rod.back, rod.front, s), bow = rod.bend * 0.22 * Math.pow(Math.max(0, along) / rod.front, 2) * rod.front * 0.18;
    pts.push([rod.gx + u[0] * along + nrm[0] * bow, rod.gy + u[1] * along + nrm[1] * bow, lerp(1.25, 0.28, Math.pow(s, 0.8))]);
  }
  ctx.save(); ctx.globalAlpha = rod.alpha; ctx.fillStyle = INK; ctx.strokeStyle = INK;
  ctx.beginPath();
  pts.forEach((q, i) => { if (i === 0) ctx.moveTo(q[0] + nrm[0] * q[2], q[1] + nrm[1] * q[2]); else ctx.lineTo(q[0] + nrm[0] * q[2], q[1] + nrm[1] * q[2]); });
  for (let j = N; j >= 0; j--) ctx.lineTo(pts[j][0] - nrm[0] * pts[j][2], pts[j][1] - nrm[1] * pts[j][2]);
  ctx.closePath(); ctx.fill();
  const rx = rod.gx + u[0] * 1.5 + nrm[0] * 3.4, ry = rod.gy + u[1] * 1.5 + nrm[1] * 3.4;   // the reel: a drum and a handle that turns
  ctx.beginPath(); ctx.arc(rx, ry, 2.5, 0, 6.2832); ctx.fill();
  ctx.lineWidth = 0.9; ctx.beginPath(); ctx.moveTo(rx, ry); ctx.lineTo(rx + Math.cos(rod.reel) * 4.2, ry + Math.sin(rod.reel) * 4.2); ctx.stroke();
  ctx.restore();
  rod.tip = pts[N];
}
function drawMan(ctx: CanvasRenderingContext2D, p: Pose) {   // the boat frame
  ctx.save(); ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineCap = "round"; ctx.lineJoin = "round";
  const px = HIP[0], py = HIP[1] + p.breathe * 0.12;
  // legs: seated, knees up, boots braced on the floor
  ([[27.5, -ANK - 4.4, false], [23.2, -ANK - 4.4, true]] as [number, number, boolean][]).forEach((F) => {
    const kn = ik(px, py, F[0], F[1], L1, L2, 1);
    ctx.fillStyle = F[2] ? INK2 : INK;
    limb(ctx, px, py, 4.4, kn[0], kn[1], 3.7); limb(ctx, kn[0], kn[1], 3.7, F[0], F[1], 2.6);
    ctx.save(); ctx.translate(F[0], F[1]); ctx.rotate(-0.1);
    ctx.beginPath(); ctx.moveTo(-3.3, -1.4); ctx.lineTo(-3.7, 2.1); ctx.quadraticCurveTo(-3.6, 3.3, -2.3, 3.3); ctx.lineTo(6.4, 3.3);
    ctx.quadraticCurveTo(8.3, 3.2, 8.1, 1.5); ctx.quadraticCurveTo(7.5, -0.3, 5.0, -0.7); ctx.lineTo(2.4, -1.5); ctx.closePath(); ctx.fill(); ctx.restore();
  });
  ctx.fillStyle = INK; ctx.strokeStyle = INK;
  // the upper body, leaning from the hip
  ctx.save(); ctx.translate(px, py); ctx.rotate(p.lean);
  const br = p.breathe * 0.35;
  ctx.beginPath(); ctx.moveTo(-2.6, -26.6);                               // a jacket in profile: neck, back, hem, belly, chest
  ctx.bezierCurveTo(-7.4, -23.5, -8.1, -13, -6.7, -3);
  ctx.bezierCurveTo(-6.5, 0.5, -7.8, 2.9, -8.5, 5.6);
  ctx.lineTo(6.6, 5.8);
  ctx.bezierCurveTo(6.2 + br, 1.2, 6.4 + br, -6, 5.8 + br, -12);
  ctx.bezierCurveTo(5.6 + br, -18.5, 4.7, -23.6, 3.3, -26.2);
  ctx.closePath(); ctx.fill();
  const sh = SHO, hn = p.handNear, hf = p.handFar;
  const en = ik(sh[0], sh[1], hn[0], hn[1], UA, FA, -1), ef = ik(sh[0] + 0.8, sh[1] + 0.2, hf[0], hf[1], UA, FA, -1);
  ctx.fillStyle = INK2; ctx.strokeStyle = INK2;
  limb(ctx, sh[0] + 0.8, sh[1] + 0.2, 2.7, ef[0], ef[1], 2.2); limb(ctx, ef[0], ef[1], 2.2, hf[0], hf[1], 1.8);
  ctx.beginPath(); ctx.arc(hf[0], hf[1], 2.0, 0, 6.2832); ctx.fill();
  ctx.fillStyle = INK; ctx.strokeStyle = INK;
  limb(ctx, sh[0], sh[1], 3.0, en[0], en[1], 2.4); limb(ctx, en[0], en[1], 2.4, hn[0], hn[1], 1.9);
  ctx.beginPath(); ctx.arc(hn[0], hn[1], 2.1, 0, 6.2832); ctx.fill();
  if (p.rodAlpha > 0.01) drawRod(ctx, p.rod); else p.rod.tip = [hn[0] + 20, hn[1] - 20];
  // head: a neck, a face in profile, a bucket hat with a floppy brim
  ctx.save(); ctx.translate(0.6, -27.4); ctx.rotate(p.headDown);
  ctx.beginPath(); ctx.moveTo(-3.0, 1.6); ctx.lineTo(-2.6, -3.4); ctx.lineTo(3.0, -3.4); ctx.lineTo(3.5, 1.6); ctx.closePath(); ctx.fill();   // neck
  ctx.beginPath(); ctx.arc(1.8, -8.1, 5.2, 0, 6.2832); ctx.fill();                                                                         // skull and face
  ctx.beginPath(); ctx.moveTo(6.3, -8.8); ctx.lineTo(8.4, -6.9); ctx.lineTo(6.4, -6.2); ctx.closePath(); ctx.fill();                         // the nose
  ctx.beginPath(); ctx.moveTo(5.2, -4.6); ctx.quadraticCurveTo(6.5, -3.6, 5.5, -2.4); ctx.lineTo(3.0, -3.2); ctx.closePath(); ctx.fill();     // the chin
  ctx.beginPath(); ctx.moveTo(-3.4, -9.7); ctx.quadraticCurveTo(-3.6, -15.8, 1.4, -16.0); ctx.quadraticCurveTo(6.7, -16.0, 6.8, -10.0); ctx.closePath(); ctx.fill();   // the crown
  ctx.save(); ctx.translate(1.6, -9.9); ctx.rotate(0.07); ctx.beginPath(); ctx.ellipse(0.7, 0, 9.4, 1.7, 0, 0, 6.2832); ctx.fill(); ctx.restore();   // the brim
  ctx.restore();
  ctx.restore();
  ctx.restore();
}

/* ───────── the line, the float, the fish: world coordinates ───────── */
interface EndPoint { x: number; y: number; mode: string; fish?: boolean; wig?: number; depth?: number; ang?: number }
function endPoint(S: FishermanScript, t: number, tipW: Pt): EndPoint {
  const fs = S.fs, L: Pt = [S.X_STOP + fs * REACH * SC, SURF];
  const hang = (extra = 7): EndPoint => ({ x: tipW[0] + fs * 1, y: tipW[1] + extra * SC, mode: "hang" });
  const c = cycleAt(S, t);
  if (!c) return hang();
  const r = c.r, rb = 1.7 + c.wait, fly = (r0: number, dur: number): Pt => { const k = clamp((r - r0) / dur); return [lerp(tipW[0], L[0], k), lerp(tipW[1], L[1], k) - 40 * SC * 4 * k * (1 - k) * (1 - 0.15 * k)]; };
  if (r < 0.85) return hang();
  if (r < 1.7) { const f1 = fly(0.85, 0.85); return { x: f1[0], y: f1[1], mode: "flight" }; }
  if (r < rb) {   // the float rides the surface, and nibbles before a bite
    const bob = Math.sin(t * 1.7) * 0.35, nibbles = c.kind === "catch" ? [[rb - 2.1, 1.3], [rb - 1.35, 1.6], [rb - 0.6, 2.2]] : [[rb - 1.2, 1.1]];
    let dip = 0;
    nibbles.forEach((n) => { const d = r - n[0]; if (d > 0 && d < 0.5) dip = Math.max(dip, n[1] * Math.sin(d / 0.5 * Math.PI)); });
    return { x: L[0] + Math.sin(t * 0.4) * 0.8, y: SURF - 0.9 + bob + dip, mode: "float" };
  }
  if (c.kind === "empty") {
    const k = clamp((r - rb) / 2.0);
    if (k >= 1) return hang();
    const run = smooth(Math.min(1, k / 0.7)), lift = smooth((k - 0.7) / 0.3);
    const x = lerp(lerp(L[0], S.X_STOP + fs * 96 * SC, run), tipW[0] + fs * 1, lift), y = lerp(SURF - 0.9, tipW[1] + 7 * SC, lift);
    return { x, y, mode: lift > 0 ? "hang" : "float" };
  }
  const fightEnd = rb + 3.3;
  if (r < rb + 0.35) { const s0 = (r - rb) / 0.35; return { x: L[0], y: SURF - 0.9 + smooth(s0) * 6, mode: "under", depth: smooth(s0) }; }
  if (r < fightEnd) {
    const f = clamp((r - rb - 0.6) / (fightEnd - rb - 0.6)), run = smooth(Math.min(1, f / 0.72)), lift = smooth((f - 0.62) / 0.38);
    let x = lerp(L[0], S.X_STOP + fs * 96 * SC, run);   // it comes in along the surface and rises in front of the stern, never across the hull
    let y = lerp(SURF + 3, SURF - 1.2, smooth(f / 0.5)) + Math.sin(t * 11) * 1.4 * (1 - run * 0.7);
    if (lift > 0) { x = lerp(x, tipW[0] + fs * 3, lift); y = lerp(y, tipW[1] + 20 * SC, lift); }
    return { x, y, mode: "fight", fish: true, wig: Math.sin(t * 17), depth: 1 - lift };
  }
  if (r < rb + 4.7) { const sw = Math.sin((r - fightEnd) * 3.1) * 3 * Math.exp(-(r - fightEnd) * 0.35); return { x: tipW[0] + fs * (3 + sw), y: tipW[1] + 20 * SC, mode: "dangle", fish: true, wig: Math.sin(t * 9) * 0.6 }; }
  if (r < rb + 5.6) {   // the release: he lets it go and it arcs back into the sea
    const k = (r - rb - 4.7) / 0.9, from: Pt = [tipW[0] + fs * 3, tipW[1] + 20 * SC], to: Pt = [S.X_STOP + fs * REACH * 0.62 * SC, SURF + 2];
    return { x: lerp(from[0], to[0], k), y: lerp(from[1], to[1], k) - 22 * SC * 4 * k * (1 - k), mode: "toss", fish: true, wig: Math.sin(t * 14), ang: lerp(-0.9, 1.1, k) };
  }
  return { x: tipW[0] + fs * 2, y: tipW[1] + (20 - smooth((r - rb - 5.6) / 0.9) * 12) * SC, mode: "hang" };   // the empty hook comes back up to the tip
}
/** the rings on the surface of one cycle, as [absolute time, distance in front of the man, size] */
function ripplesOf(kind: CycleKind, wait: number, start: number): number[][] {
  const rb = 1.7 + wait, out = [[start + 1.7, REACH, 1], [start + 1.7 + 3.6, REACH, 0.5]];
  if (kind === "catch") out.push([start + rb - 2.1, REACH, 0.45], [start + rb - 1.35, REACH, 0.5], [start + rb - 0.6, REACH, 0.6], [start + rb + 0.1, REACH, 1.1], [start + rb + 5.45, REACH * 0.62, 1.2]);
  else out.push([start + rb - 1.2, REACH, 0.45], [start + 1.7 + 7.2, REACH, 0.45]);
  return out;
}
/** the rings that can still be showing at time t: this cycle's, and the one before it (a splash outlasts its cycle) */
function ripples(S: FishermanScript, t: number): number[][] {
  const c = cycleAt(S, t);
  if (!c) return [];
  const prev = CYCLES[(c.index + CYCLES.length - 1) % CYCLES.length], out = ripplesOf(c.kind, c.wait, c.start), prevStart = c.start - prev.len;
  return prevStart >= S.tc - 1e-9 ? out.concat(ripplesOf(prev.kind, prev.wait, prevStart)) : out;
}

/* ───────── the sea: the footer's own top edge ───────── */
let layer: HTMLCanvasElement | null = null;
function layerFor(w: number, h: number): HTMLCanvasElement {
  if (!layer) layer = document.createElement("canvas");
  if (layer.width !== w || layer.height !== h) { layer.width = w; layer.height = h; }
  return layer;
}
function drawSea(ctx: CanvasRenderingContext2D, t: number, a: number, x0: number, x1: number) {
  ctx.save(); ctx.beginPath(); ctx.rect(x0, SURF - 0.5, x1 - x0, 8); ctx.clip(); ctx.lineCap = "round";
  ctx.beginPath();
  for (let x = x0; x <= x1; x += 6) { const y = SURF + 0.7 + Math.sin(x * 0.085 + t * 1.15) * 0.45 + Math.sin(x * 0.031 - t * 0.6) * 0.5; if (x === x0) ctx.moveTo(x, y); else ctx.lineTo(x, y); }
  ctx.lineWidth = 0.9; ctx.strokeStyle = `rgba(255,255,255,${0.2 * a})`; ctx.stroke();
  for (let i = 0; i < 26; i++) {
    const gx = (i * 41.7 + t * (2.2 + (i % 5) * 0.8) + (i % 3) * 120) % (x1 - x0 + 80) + x0 - 40, tw = Math.sin(t * (0.5 + (i % 4) * 0.17) + i * 2.1), al = Math.max(0, tw) * (0.22 + (i % 3) * 0.06) * a, gy = SURF + 1.7 + (i % 3) * 1.5, ln = 3 + (i % 4) * 2.2;
    if (al < 0.01) continue;
    ctx.beginPath(); ctx.moveTo(gx - ln / 2, gy); ctx.lineTo(gx + ln / 2, gy); ctx.lineWidth = 0.7; ctx.strokeStyle = `rgba(255,255,255,${al})`; ctx.stroke();
  }
  ctx.restore();
}
function drawFish(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, ang: number, wig: number, fill: string) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(ang); ctx.fillStyle = fill;
  ctx.beginPath(); ctx.moveTo(len * 0.5, 0); ctx.bezierCurveTo(len * 0.28, -len * 0.19, -len * 0.18, -len * 0.17, -len * 0.34, -len * 0.03);
  ctx.lineTo(-len * 0.52, -len * (0.2 + 0.07 * wig)); ctx.lineTo(-len * 0.46, 0); ctx.lineTo(-len * 0.52, len * (0.2 - 0.07 * wig)); ctx.lineTo(-len * 0.34, len * 0.03);
  ctx.bezierCurveTo(-len * 0.18, len * 0.17, len * 0.28, len * 0.19, len * 0.5, 0); ctx.closePath(); ctx.fill(); ctx.restore();
}

export interface FishermanDrawOptions {
  S: FishermanScript;
  /** 0 to 1: the whole scene's own fade */
  alpha: number;
  /** the canvas's own pixel size, for the reflection layer */
  cw: number; ch: number;
  /** the horizontal extent to draw the sea over, in stage units */
  x0: number; x1: number;
  /** nothing is drawn deeper than this many stage units below the surface: the gap above the first line of text, less a margin */
  maxBelow: number;
}

/**
 * Draws one moment of the scene. `ctx` must already be set so that one stage unit is one unit of the context, with y = SURF
 * on the footer's top edge. `t` is seconds since the boat appeared.
 */
export function drawFisherman(ctx: CanvasRenderingContext2D, t: number, o: FishermanDrawOptions) {
  const S = o.S, a = clamp(o.alpha), fs = S.fs;
  if (a <= 0.003) return;
  const p = poseAt(S, t), F = frameAt(S, t);
  // THE GUARANTEE THAT HE NEVER REACHES A NUMBER: the whole scene is clipped to a band that ends maxBelow units under the
  // footer's top edge. The clip is in device space, so the context's later resets of its transform do not undo it.
  ctx.save();
  ctx.beginPath(); ctx.rect(o.x0, SURF - 1000, o.x1 - o.x0, 1000 + Math.max(0, o.maxBelow)); ctx.clip();
  drawSea(ctx, t, a, o.x0, o.x1);
  // the boat and the man go to a layer first: one flat colour, and drawing it straight to the canvas at a partial alpha would show
  // every overlap darker. The layer is then drawn once, and once more upside down in the water.
  const tf = ctx.getTransform(), L = layerFor(o.cw, o.ch), lc = L.getContext("2d");
  if (!lc) return;
  lc.setTransform(1, 0, 0, 1, 0, 0); lc.clearRect(0, 0, L.width, L.height); lc.setTransform(tf);
  lc.save(); lc.translate(F.X, F.y); lc.scale(fs * SC, SC); lc.rotate(F.pitch);
  drawBoat(lc, p); drawMan(lc, p); drawOar(lc, p.oar);
  lc.restore();
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = a; ctx.drawImage(L, 0, 0); ctx.restore();

  // the line, the float, the fish
  const tipT = p.rod.tip as number[], tipB = torsoToBoat(p.lean, [tipT[0], tipT[1]]), tipW = toWorld(F, tipB[0], tipB[1]), E = endPoint(S, t, tipW), ew: Pt = [E.x, E.y];
  ctx.save(); ctx.globalAlpha = a;
  const slack = E.mode === "float" ? 6 : E.mode === "fight" ? 0.8 : E.mode === "flight" ? 3.5 : 1.2;
  if (E.mode !== "toss" && p.rodAlpha > 0.5) {
    const dx = ew[0] - tipW[0], dy = ew[1] - tipW[1];
    ctx.beginPath(); ctx.moveTo(tipW[0], tipW[1]); ctx.quadraticCurveTo(tipW[0] + dx * 0.5, tipW[1] + dy * 0.5 + slack * (1 + Math.abs(dx) / 80), ew[0], ew[1]);
    ctx.lineWidth = 0.55; ctx.strokeStyle = LINE; ctx.stroke();
  }
  ripples(S, t).forEach((r) => {
    const age = t - r[0];
    if (age < 0 || age > 2.6) return;
    const u = age / 2.6, x = S.X_STOP + fs * r[1] * SC;
    ctx.save(); ctx.beginPath(); ctx.rect(x - 70, SURF, 140, 14); ctx.clip();
    for (let k = 0; k < 3; k++) {
      const uu = u - k * 0.13;
      if (uu <= 0) continue;
      ctx.beginPath(); ctx.ellipse(x, SURF + 0.4, (4 + uu * 32) * Math.sqrt(r[2]), 0.5 + uu * 3.0 * Math.sqrt(r[2]), 0, 0, Math.PI);
      ctx.lineWidth = 0.55; ctx.strokeStyle = `rgba(255,255,255,${(1 - uu) * 0.55 * a})`; ctx.stroke();
    }
    ctx.restore();
    if (age < 0.55 && r[2] >= 1) {   // the drops of a splash, in the colour of the sea
      for (let d = 0; d < 7; d++) {
        const an = -0.4 - d * 0.38, sp = 16 + (d % 3) * 7, dx2 = Math.cos(an) * sp * age * (d % 2 ? 1 : -1) * 0.7, dy2 = Math.sin(an) * sp * age + 40 * age * age;
        if (dy2 < 0.5) { ctx.beginPath(); ctx.arc(x + dx2, SURF + dy2, Math.max(0.01, 0.75 * (1 - age / 0.7)), 0, 6.2832); ctx.fillStyle = `rgba(11,80,110,${0.8 * (1 - age / 0.55)})`; ctx.fill(); }
      }
    }
  });
  // the float: the slate colour above the surface (it shows on the page), pale below it (it shows on the bar)
  if (E.mode === "float" || E.mode === "under" || E.mode === "flight") {
    const bx = ew[0];
    let by = Math.min(ew[1], SURF + 5), sub = E.mode === "under" ? (E.depth as number) : 0;
    if (E.mode === "flight" || by < SURF - 1.2) { by = ew[1]; sub = 0; }
    const vis = 1 - clamp(sub * 1.15);
    if (vis > 0.02) {
      ctx.save(); ctx.globalAlpha = a * vis; ctx.strokeStyle = LINE; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(bx, by - 2.4); ctx.lineTo(bx, by - 5.4); ctx.stroke();
      ctx.beginPath(); ctx.arc(bx, by, 2.3, Math.PI, 0); ctx.closePath(); ctx.fillStyle = INK; ctx.fill();
      if (E.mode !== "flight") { ctx.beginPath(); ctx.arc(bx, by, 2.3, 0, Math.PI); ctx.closePath(); ctx.fillStyle = PALE; ctx.fill(); }
      ctx.restore();
    }
  }
  // the fish: pale under the water while it takes the bait (it shows on the bar), slate once it is out
  if (E.fish && E.mode !== "toss") {
    const dark = E.mode === "dangle" || (E.mode === "fight" && E.y < SURF - 3);
    ctx.save(); ctx.globalAlpha = a * (E.mode === "fight" && (E.depth as number) > 0.5 ? 0.8 : 1);
    drawFish(ctx, ew[0], ew[1] + 1, 11 * SC, dark ? Math.PI / 2 + (E.mode === "dangle" ? Math.sin(t * 3.1) * 0.12 : 0.4) : (fs > 0 ? 0 : Math.PI) + Math.sin(t * 9) * 0.18, E.wig || 0, dark ? INK : PALE);
    ctx.restore();
  }
  if (E.mode === "toss") { ctx.save(); ctx.globalAlpha = a; drawFish(ctx, ew[0], ew[1], 11 * SC, (E.ang || 0) * fs + (fs > 0 ? 0 : Math.PI), E.wig || 0, INK); ctx.restore(); }
  ctx.restore();

  // the reflection: the layer, upside down, wobbling and fading with depth, only in the top few units of the bar (the numbers are never drawn over)
  const sd = tf.d, ys = SURF * sd + tf.f, depth = Math.min(6, Math.max(0, o.maxBelow)) * sd, W = L.width;
  let xr0 = Math.max(0, (F.X - 120) * tf.a + tf.e), xr1 = Math.min(W, (F.X + 120) * tf.a + tf.e);
  if (xr0 > xr1) { const sw2 = xr0; xr0 = xr1; xr1 = sw2; }
  if (a > 0.05 && xr1 > xr0) {
    ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.beginPath(); ctx.rect(0, ys, W, depth); ctx.clip();
    const step = Math.max(1, Math.round(sd * 0.8));
    for (let yy = 0; yy < depth; yy += step) {
      const src = ys - yy - step, wob = Math.sin(yy / sd * 0.9 + t * 2.3) * 1.1 * sd + Math.sin(yy / sd * 0.37 - t * 1.1) * 0.9 * sd;
      ctx.globalAlpha = a * 0.34 * (1 - yy / depth);
      ctx.drawImage(L, xr0, src, xr1 - xr0, step, xr0 + wob, ys + yy, xr1 - xr0, step);
    }
    ctx.restore();
  }
  ctx.restore();   // the clip
}

export { BARB as FISHERMAN_BARB };

/** the sea (the footer's own top edge: a slow crest line and a few glints), shared with the other things that move along the bar */
export { drawSea as drawBarSea };

/** for tests: the pose at a moment, and how long the pattern of casts is before it repeats */
export { poseAt as fishermanPose };
export const FISHERMAN_PATTERN_LEN = PATTERN_LEN;
